import {LatestCommitProvider} from '@github-ui/code-view-shared/contexts/LatestCommitContext'
import type {
  DataRouterBlamePayload,
  DataRouterBlobLayoutPayload,
  DataRouterBlobPayload,
} from '@github-ui/code-view-types'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {isResponseError} from '@github-ui/react-core/future/response-error'
import {useChildRouteQuery, useRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {Outlet, useMatches, useSearchParams} from '@github-ui/react-router'
import {isUIService} from '@github-ui/runtime-environment'
import {type ReactNode, useMemo, useRef} from 'react'

import type {GoToLineSelection} from '../components/blob/BlobNavigationButtons'
import {BlobHeaderActions, CodeViewHeaderDataRouter} from '../components/headers/CodeViewHeader'
import {TaillightsBlobContext, TaillightsSelectionContext} from '../components/taillights/source-context'
import {DeferredASTProvider, useDeferredASTQuery} from '../contexts/DeferredASTContext'
import {DeferredMetadataProvider, EmptyDeferredMetadata} from '../contexts/DeferredMetadataContext'
import {DeferredRichTextProvider, useDeferredRichTextQuery} from '../contexts/DeferredRichTextContext'
import {FindInFileOpenProvider} from '../contexts/FindInFileOpenContext'
import {SearchTermProvider} from '../contexts/SearchTermContext'
import {type CurrentBlob, CurrentBlobProvider} from '../hooks/CurrentBlob'
import {CurrentStyledBlobProvider} from '../hooks/CurrentStyledBlob'
import {useShouldUseTaillights} from '../hooks/use-should-use-taillights'
import {codeViewBlameRoute} from '../routes/code-view-blame-route'
import {codeViewBlobLayoutRoute} from '../routes/code-view-blob-layout-route'
import {codeViewBlobRoute} from '../routes/code-view-blob-route'

const isRateLimitError = (error: Error) =>
  (error as Error & {status?: number}).status === 429 || (isResponseError(error) && error.response.status === 429)

// Only escalate a rate limit when there is no cached StyledBlob to show. A failed
// background refetch keeps the existing code visible instead of replacing the page.
const shouldThrowRateLimitError = (error: Error, query: {state: {data: unknown}}) =>
  isUIService && query.state.data === undefined && isRateLimitError(error)

function TaillightsBlobInteractionProviders({isBlob, children}: {isBlob: boolean; children: ReactNode}) {
  const taillights = useShouldUseTaillights()
  const selectionRef = useRef<(() => GoToLineSelection | null) | null>(null)
  return (
    <TaillightsBlobContext value={isBlob && taillights}>
      <TaillightsSelectionContext value={selectionRef}>{children}</TaillightsSelectionContext>
    </TaillightsBlobContext>
  )
}

function mergeBlobPayloads(
  layoutData: DataRouterBlobLayoutPayload,
  routeData: DataRouterBlobPayload | DataRouterBlamePayload,
  isPlainSearchParam: boolean,
): CurrentBlob {
  const {headerInfo: layoutHeaderInfo, ...layoutBlob} = layoutData.blob

  // blame doesn't have blob data to merge
  if ('blame' in routeData) {
    return {
      csv: null,
      csvError: null,
      richText: null,
      richTextTruncated: false,
      symbols: {not_analyzed: true, timed_out: false, symbols: []},
      renderedFileInfo: null,
      issueTemplate: null,
      discussionTemplate: undefined,
      ...layoutBlob,
      codeLineWrapEnabled: layoutData.codeLineWrapEnabled,
      headerInfo: {
        ...layoutHeaderInfo,
        toc: null,
      },
      isPlain: layoutBlob.isPlain || isPlainSearchParam,
    }
  }

  const {headerInfo: routeHeaderInfo, ...routeBlob} = routeData as DataRouterBlobPayload

  return {
    ...layoutBlob,
    ...routeBlob,
    codeLineWrapEnabled: layoutData.codeLineWrapEnabled,
    headerInfo: {
      ...layoutHeaderInfo,
      ...routeHeaderInfo,
    },
    isPlain: layoutBlob.isPlain || isPlainSearchParam,
  }
}

export function CodeViewBlobLayout() {
  const {data: layoutData} = useRouteQuery(codeViewBlobLayoutRoute, 'mainQuery')
  const matches = useMatches()
  const isBlob = !!matches.find(match => match.id === codeViewBlobRoute.id)

  const {data: routeData} = useChildRouteQuery(isBlob ? codeViewBlobRoute : codeViewBlameRoute, 'mainQuery')
  const {data: styledBlobData} = useRouteQuery(codeViewBlobLayoutRoute, 'codeViewBlobLayoutRoute.StyledBlob', {
    throwOnError: shouldThrowRateLimitError,
  })
  const {data: deferredMetadataQuery} = useRouteQuery(codeViewBlobLayoutRoute, 'deferredMetadata')
  const {
    data: latestCommit,
    error: latestCommitError,
    isLoading: latestCommitLoading,
  } = useRouteQuery(codeViewBlobLayoutRoute, 'latestCommit')

  const deferredAST = useDeferredASTQuery(
    layoutData.refInfo,
    layoutData.path,
    layoutData.error?.httpStatus === 404,
    styledBlobData?.rawLines?.length ?? 100000,
    styledBlobData?.stylingDirectives,
    isBlob,
  )

  const richTextTruncated = routeData && 'richTextTruncated' in routeData ? routeData.richTextTruncated : false
  const deferredRichText = useDeferredRichTextQuery(layoutData.refInfo, layoutData.path, richTextTruncated)

  const [urlParams] = useSearchParams()
  const isPlainSearchParam = urlParams.get('plain') === '1'

  const blob = useMemo(
    () =>
      routeData && layoutData && !layoutData.error
        ? mergeBlobPayloads(layoutData, routeData, isPlainSearchParam)
        : null,
    [layoutData, routeData, isPlainSearchParam],
  )

  if (layoutData?.error) {
    return <Outlet />
  }

  if (!routeData || !layoutData || !blob) {
    return null
  }

  const deferredMetadata = deferredMetadataQuery ?? EmptyDeferredMetadata
  const content = (
    <>
      <CodeViewHeaderDataRouter
        narrowActions={<BlobHeaderActions payload={layoutData} narrow />}
        wideActions={<BlobHeaderActions payload={layoutData} narrow={false} />}
      />
      <Outlet />
    </>
  )

  return (
    <LatestCommitProvider latestCommit={latestCommit} error={latestCommitError} isLoading={latestCommitLoading}>
      <DeferredMetadataProvider {...deferredMetadata}>
        <DeferredASTProvider {...deferredAST}>
          <DeferredRichTextProvider richText={deferredRichText.richText}>
            <CurrentBlobProvider blob={blob}>
              <CurrentStyledBlobProvider blob={styledBlobData}>
                <SearchTermProvider>
                  <FindInFileOpenProvider isBlame={!isBlob}>
                    {isFeatureEnabled('code_view_enable_taillights') ? (
                      // Keep the header mounted when wrapping or blame changes eligibility within the trial.
                      <TaillightsBlobInteractionProviders isBlob={isBlob}>{content}</TaillightsBlobInteractionProviders>
                    ) : (
                      content
                    )}
                  </FindInFileOpenProvider>
                </SearchTermProvider>
              </CurrentStyledBlobProvider>
            </CurrentBlobProvider>
          </DeferredRichTextProvider>
        </DeferredASTProvider>
      </DeferredMetadataProvider>
    </LatestCommitProvider>
  )
}
